// Set of helper methods to enable feature previews on the frontend
import _ from "underscore";
import { isBrowser } from "../../core/detection";
import { formatQueryString, getQueryStringValue, getQueryStringValues } from "@travellocal/utils/urlHelpers";
import { getCookie, setCookie } from "./cookieHelper";
import moment from "moment";

const FLAGS_COOKIE = "feature_flags";
const FLAGS_QS_KEY = "preview";
const FLAGS_SEP = ",";

/**
 * Feature flags currently used in the code.
 * They should be named as follows: {DESCRIPTIVE NAME}_{GITHUB ISSUE NUMBER}
 * The value should be URL friendly - no special symbols.
 * When they're no longer used, delete the entry from this list.
 */
export const enum FeatureFlag {
  EXAMPLE = "example",
  SLATE_EDITOR_1529 = "slate_editor",
  SLATE_GRAMMARLY_2348 = "grammarly",
  CUSTOMER_ITINERARY_3008 = "customer_itinerary",
  LTO_ENQUIRY_WORKFLOW_3036 = "enquiry_workflow",
  NEXTJS_3358 = "nextjs",
  SIMS_REBRAND_3387 = "rebrand_2021"
}

const saveFlags = (flags: string[] = []) => {
  if (flags.length) {
    setCookie(FLAGS_COOKIE, flags.join(FLAGS_SEP), moment().add(1, "year"));
  }
  else {
    setCookie(FLAGS_COOKIE, "", moment().subtract(1, "year"));
  }
  
  console.debug(`Set feature flags: [${flags.join(", ")}]`)
};

/**
 * Flags are cached in a cookie when first checked for.
 * Flags can be cleared by having key present in QS with no value - ?preview=
 * or by deleting the cookie.
 */
export const isFlagActive = (flag: FeatureFlag): boolean => {
  if (!isBrowser()) {
    // Only client side render supported
    return;
  }

  const flagsString = getQueryStringValue(FLAGS_QS_KEY);

  if (flagsString != null) {
    // Cache flags if they're provided in the query string
    const qsFlags = (flagsString || "").split(FLAGS_SEP);
    saveFlags(qsFlags);
  }

  const lsFlags = getCookie(FLAGS_COOKIE) as string || "";
  return lsFlags.indexOf(flag) > -1;
};

/**
 * Remove multiple flags from the feature flags list.
 */
export const clearFlags = (flags: string[]): void => {
  if (!isBrowser()) {
    // Only client side render supported
    return;
  }

  const activeFlags = getCookie(FLAGS_COOKIE) as string || "";
  const newFlags = _.difference(activeFlags.split(FLAGS_SEP), flags);
  saveFlags(newFlags);

  // Refresh browser so flag takes effect
  // Remove preview flag so it's not re-applied
  const qsValues = getQueryStringValues();
  delete qsValues.preview;
  window.location.search = formatQueryString(qsValues);
};

/**
 * Remove a single flag from the feature flags list.
 */
export const clearFlag = (flag: string): void => clearFlags([flag]);
